/**
 * @author Emma (epolize) Politzer
 * @description Maps a number from one range to another
 * @param num - The number to map
 * @param inMin - The minimum of the input range
 * @param inMax - The maximum of the input range
 * @param outMin - The minimum of the output range
 * @param outMax - The maximum of the output range
 * @returns The mapped number
 */
export const mapNumRange = (num, inMin, inMax, outMin, outMax) => {
    return ((num - inMin) * (outMax - outMin)) / (inMax - inMin) + outMin;
}

/**
 * @author Emma (epolitze) Politzer
 * @description Compare two arrays
 * @param array1 {Array} - The first array
 * @param array2 {Array} - The second array
 * @returns boolean - true if the arrays are equal, false otherwise
 */
export function compArray(array1, array2) {
    if (array1.length !== array2.length) {
        return false
    }
    for (let i = 0; i < array1.length; i++) {
        if (array1[i] !== array2[i]) {
            return false
        }
    }
    return true
}

/**
 * @author Corentin (ccharton) Charton
 * @description Linear interpolation between two vectors 3
 * @param a {Object} - The start vector
 * @param b {Object} - The end vector
 * @param t {number} - The interpolation value
 * @returns {{x: number, y: number, z: number}} - The interpolated vector
 */
export function lerpVector3(a, b, t) {
    return {
        x: a.x * (1 - t) + b.x * t,
        y: a.y * (1 - t) + b.y * t,
        z: a.z * (1 - t) + b.z * t
    }
}

/**
 * @author Corentin (ccharton) Charton
 * @description Linear interpolation between two quaternions
 * @param a {Object} - The start quaternion
 * @param b {Object} - The end quaternion
 * @param t {number} - The interpolation value
 * @returns {{x: number, y: number, z: number, w: number}} - The interpolated quaternion
 */
export function lerpQuaternion(a, b, t) {
    return {
        x: a.x * (1 - t) + b.x * t,
        y: a.y * (1 - t) + b.y * t,
        z: a.z * (1 - t) + b.z * t,
        w: a.w * (1 - t) + b.w * t
    }
}

/**
 * @author Corentin (ccharton) Charton
 * @param start {number} - The start value
 * @param end {number} - The end value
 * @param t {number} - The interpolation value
 * @returns {number} - The interpolated value
 */
export function lerp(start, end, t) {
    return start + (end - start) * t;
}

/**
 * @author Corentin (ccharton) Charton
 * @description Filter an object
 * @param objectToFilter {Object} - The object to filter
 * @param callback {Function} - The function that will be used to filter
 * @returns {object} - The filtered object
 */
export function filterObject(objectToFilter, callback) {
    return Object.fromEntries(Object.entries(objectToFilter).
    filter(([key, value]) => callback(key, value)));
}

/**
 * @author Corentin (ccharton) Charton
 * @description Create a video tag (ready to use for three.js VideoTexture)
 * @param src {string} - The path for the video
 * @returns {HTMLVideoElement} - The video tag
 */
export function createVideoTag(src) {
    const video = document.createElement('video')
    video.src = src
    video.loop = true
    video.muted = true
    video.disablePictureInPicture = true
    video.playsInline = true
    video.preload = 'auto'

    return video
}

/**
 * @author Corentin (ccharton) Charton
 * @description Get the current language wanted by the user.
 * @returns {string} - The current language or english if value not set / valid.
 */
export function getCurrentLanguage() {
    const lang = localStorage.getItem('language') || 'en'
    return lang.match(/^en$|fr$|de$|es$/) ? lang : 'en'
}